import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SharedService } from '@myproject/shared/shared.service';
import { AlDashboardService } from './al-dashboard.service';
import { Chart, registerables } from 'chart.js';
import { DatePipe } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { environment } from '@myenv/environment';
import * as FileSaver from 'file-saver';

@Component({
    selector: 'app-al-dashboard',
    templateUrl: './al-dashboard.component.html',
    styleUrls: ['./al-dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AlDashboardComponent implements OnInit {

  isLoading = false;
  currentUser: any = {};
  notReportingDevices = [];
  notReportingData: any = [];
  deviceCharts = {
    nrd30Chart: null,
    nrd15Chart: null,
    nrd7Chart: null,
    nrd2Chart: null,
    nrd1Chart: null
  };
  batteryStatus: any = {};
  vPacketData: any = {};
  networkLatencyData: any = {};
  deviceExpiryData: any = {};
  date: string;
  last7Date: string;
  showPreviousData = false;

  constructor(
    private alDashboardService: AlDashboardService,
    private sharedService: SharedService,
    private title: Title,
  ) { }

  ngOnInit() {
    Chart.register(...registerables);
    this.title.setTitle('Dashboard' + environment.title_text);
    this.currentUser = JSON.parse(localStorage.getItem('currentUser'));
    this.selectDay('current');
  }

  onToggle() {
    if (this.showPreviousData) {
      this.selectDay('previous');
    } else {
      this.selectDay('current');
    }
  }

  selectDay(day) {
    const t = new Date();
    const y = new Date(t);
    y.setDate(y.getDate() - 1);
    const s = new Date(t);
    s.setDate(s.getDate() - 7);
    if (day === 'current') {
      this.date = `${t.getFullYear()}-${t.getMonth() + 1}-${t.getDate()}`;
      this.last7Date = `${s.getFullYear()}-${s.getMonth() + 1}-${s.getDate()}`;
    } else if (day === 'previous') {
      this.date = `${y.getFullYear()}-${y.getMonth() + 1}-${y.getDate()}`;
      this.last7Date = `${s.getFullYear()}-${s.getMonth() + 1}-${s.getDate()}`;
    }
    this.refresh();
  }

  refresh() {
    this.isLoading = true;
    this.getNotReportingDevices();
    this.getBatteryStatus();
    this.getVPacketData();
    this.getNetworkLatencyData();
    this.getDeviceExpiryStatus();
  }

  getNotReportingDevices() {
    this.alDashboardService.getNotReportingDevices(this.last7Date, this.date).subscribe({
      next: res => {
        this.isLoading = false;
        this.sortChartData(res);
        if (res.length) {
          this.notReportingData = res[0];
        } else {
          this.notReportingData = {};
        }
      },
      error: error => {
        this.isLoading = false;
        this.sharedService.getErrorMsg(error);
      }
    });
  }

  getBatteryStatus() {
    this.alDashboardService.getBatteryStatus().subscribe({
      next: res => {
        this.batteryStatus = res;
      },
      error: error => {
        this.sharedService.getErrorMsg(error);
      }
    });
  }

  getVPacketData() {
    this.alDashboardService.getVPacketData().subscribe({
      next: res => {
        this.vPacketData = res;
      },
      error: error => {
        this.sharedService.getErrorMsg(error);
      }
    });
  }

  getNetworkLatencyData() {
    this.alDashboardService.getNetworkLatencyData(this.date, this.date).subscribe({
      next: res => {
        if (res.length) {
          this.networkLatencyData = res[0];
        } else {
          this.networkLatencyData = {};
        }
      },
      error: error => {
        this.sharedService.getErrorMsg(error);
      }
    });
  }

  getDeviceExpiryStatus() {
    this.alDashboardService.getDeviceExpiryStatus(this.currentUser.customerId).subscribe({
      next: res => {
        this.deviceExpiryData = res;
      },
      error: error => {
        this.sharedService.getErrorMsg(error);
      }
    });
  }

  sortChartData(data) {
    const labels = [];
    // const nrdChart = { ignitionOn: [], ignitionOff: [] };
    const nrd30Chart = { ignitionOn: [], ignitionOff: [] };
    const nrd15Chart = { ignitionOn: [], ignitionOff: [] };
    const nrd7Chart = { ignitionOn: [], ignitionOff: [] };
    const nrd2Chart = { ignitionOn: [], ignitionOff: [] };
    const nrd1Chart = { ignitionOn: [], ignitionOff: [] };
    data.map(item => {
      let datePipe = new DatePipe('en-US');
      let eventDate = datePipe.transform(item.bucket, 'EE, dd MMM');
      labels.push(eventDate);
      // not reporting 7 days
      if (item.moreThan7Days) {
        nrd7Chart.ignitionOn.push(item.moreThan7Days.ignitionOn);
        nrd7Chart.ignitionOff.push(item.moreThan7Days.ignitionOff);
      } else {
        nrd7Chart.ignitionOn.push(0);
        nrd7Chart.ignitionOff.push(0);
      }
      // not reporting 15 days
      if (item.moreThan15Days) {
        nrd15Chart.ignitionOn.push(item.moreThan15Days.ignitionOn);
        nrd15Chart.ignitionOff.push(item.moreThan15Days.ignitionOff);
      } else {
        nrd15Chart.ignitionOn.push(0);
        nrd15Chart.ignitionOff.push(0);
      }
      // not reporting 30 days
      if (item.moreThan30Days) {
        nrd30Chart.ignitionOn.push(item.moreThan30Days.ignitionOn);
        nrd30Chart.ignitionOff.push(item.moreThan30Days.ignitionOff);
      } else {
        nrd30Chart.ignitionOn.push(0);
        nrd30Chart.ignitionOff.push(0);
      }
      // not reporting 2 days
      if (item.moreThan2Days) {
        nrd2Chart.ignitionOn.push(item.moreThan2Days.ignitionOn);
        nrd2Chart.ignitionOff.push(item.moreThan2Days.ignitionOff);
      } else {
        nrd2Chart.ignitionOn.push(0);
        nrd2Chart.ignitionOff.push(0);
      }
      // not reporting 1 day
      if (item.moreThan1Day) {
        nrd1Chart.ignitionOn.push(item.moreThan1Day.ignitionOn);
        nrd1Chart.ignitionOff.push(item.moreThan1Day.ignitionOff);
      } else {
        nrd1Chart.ignitionOn.push(0);
        nrd1Chart.ignitionOff.push(0);
      }
    });
    // this.drawLineGraph('nrdChart', labels, nrdChart.ignitionOn, nrdChart.ignitionOff);
    this.drawLineGraph('nrd30Chart', labels, nrd30Chart.ignitionOn, nrd30Chart.ignitionOff);
    this.drawLineGraph('nrd15Chart', labels, nrd15Chart.ignitionOn, nrd15Chart.ignitionOff);
    this.drawLineGraph('nrd7Chart', labels, nrd7Chart.ignitionOn, nrd7Chart.ignitionOff);
    this.drawLineGraph('nrd2Chart', labels, nrd2Chart.ignitionOn, nrd2Chart.ignitionOff);
    this.drawLineGraph('nrd1Chart', labels, nrd1Chart.ignitionOn, nrd1Chart.ignitionOff);
  }

  drawLineGraph(container, labels, data1, data2) {
    if (this.deviceCharts[container]) {
      this.deviceCharts[container].destroy();
    }
    let ctx = document.getElementById(container) as HTMLCanvasElement;
    this.deviceCharts[container] = new Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Ignition On',
            data: data1,
            borderColor: '#172b4d',
            fill: false,
            borderWidth: 2,
            pointRadius: 1
          },
          {
            label: 'Ignition Off',
            data: data2,
            borderColor: '#86898e',
            fill: false,
            borderWidth: 2,
            pointRadius: 1
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            position: 'bottom',
            display: false
          },
          tooltip: {
            // Disable the on-canvas tooltip
            enabled: false,
            callbacks: {
              labelColor: (ctx) => ({
                  borderColor: 'rgb(255, 0, 0)',
                  backgroundColor: 'rgb(255, 0, 0)',
              }),
            labelTextColor: () => '#543453'
            },
  
            external: (ctx) => {
              const tooltipModel = ctx.tooltip;
              // Tooltip Element
              var tooltipEl: any = document.getElementById('chartjs-tooltip');
  
              // Create element on first render
              if (!tooltipEl) {
                tooltipEl = document.createElement('div');
                tooltipEl.id = 'chartjs-tooltip';
                tooltipEl.innerHTML = '<table></table>';
                document.body.appendChild(tooltipEl);
              }
  
              // Hide if no tooltip
              if (tooltipModel.opacity === 0) {
                tooltipEl.style.opacity = 0;
                return;
              }
  
              // Set caret Position
              tooltipEl.classList.remove('above', 'below', 'no-transform');
              if (tooltipModel.yAlign) {
                tooltipEl.classList.add(tooltipModel.yAlign);
              } else {
                tooltipEl.classList.add('no-transform');
              }
  
              function getBody(bodyItem) {
                return bodyItem.lines;
              }
  
              // Set Text
              if (tooltipModel.body) {
                var titleLines = tooltipModel.title || [];
                var bodyLines = tooltipModel.body.map(getBody);
  
                var innerHtml = '<thead>';
  
                titleLines.forEach(function (title) {
                  innerHtml += '<tr><th>' + title + '</th></tr>';
                });
                innerHtml += '</thead><tbody>';
  
                bodyLines.forEach(function (body, i) {
                  var colors = tooltipModel.labelColors[i];
                  var style = 'background:' + colors.backgroundColor;
                  style += '; border-color:' + colors.borderColor;
                  style += '; border-width: 2px';
                  var span = '<span style="' + style + '"></span>';
                  innerHtml += '<tr><td>' + span + body + '</td></tr>';
                });
                innerHtml += '</tbody>';
  
                var tableRoot = tooltipEl.querySelector('table');
                tableRoot.innerHTML = innerHtml;
              }
  
              // `this` will be the overall tooltip
              var position = ctx.chart.canvas.getBoundingClientRect();
  
              // Display, position, and set styles for font
              tooltipEl.style.opacity = 1;
              tooltipEl.style.position = 'absolute';
              tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
              tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
              const font = (Chart as any).helpers.toFont(tooltipModel.options.bodyFont);
              tooltipEl.style.fontFamily = font.family;
              tooltipEl.style.fontSize = font.size + 'px';
              tooltipEl.style.fontStyle = font.style;
              const pad = (tooltipModel as any).padding ?? {x:0,y:0}
              tooltipEl.style.padding = pad.y + 'px ' + pad.x + 'px';
              tooltipEl.style.pointerEvents = 'none';
              tooltipEl.style.color = '#ffff';
              tooltipEl.style.backgroundColor = 'rgba(29, 28, 26, 0.8)';
              tooltipEl.style.borderRadius = '3px';
            }
          }
        },
        scales: {
          x: {
            display: false,
          },
          y: {
            display: true,
            suggestedMin: 0,
            suggestedMax: 10,
          }
        },
      }
    });
  }

  downloadNRDReport() {
    this.alDashboardService.downloadNRDReport().subscribe(data => {
      const contentDisposition = data.headers.get('Content-Disposition');
      let filename;
      if (contentDisposition == null) {
        filename = this.date;
      } else {
        filename = contentDisposition.split(';')[1].split('filename')[1].split('=')[1].trim();
      }
      const blob = new Blob([data.body]);
      FileSaver.saveAs(blob, `${filename}.csv.gz`);
    });
  }

  downloadNRDDevices(days) {
    this.alDashboardService.downloadNRDDevices(days).subscribe(data => {
      this.saveCsv(data);
    });
  }

  downloadVPacketReport() {
    this.alDashboardService.downloadVPacketReport().subscribe(data => {
      this.saveCsv(data);
    });
  }

  saveCsv(data) {
    const contentDisposition = data.headers.get('Content-Disposition');
    let filename;
    if (contentDisposition == null) {
      filename = this.date;
    } else {
      filename = contentDisposition.split(';')[1].split('filename')[1].split('=')[1].trim();
    }
    const blob = new Blob([data.body], {
      type: 'application/csv'
    });
    FileSaver.saveAs(blob, `${filename}`);
  }
}
